import { useEffect, useRef } from 'react';
import { useGroupChannel } from '@sendbird/uikit-react/GroupChannel/context';
import { getFirstUnreadStartingPoint } from '../../utils/sendbird/firstUnreadScroll';

/**
 * Scrolls to the first unread message once per channel open.
 * Cancels if the user scrolls manually before the auto-scroll runs.
 */
const SendbirdScrollToFirstUnreadOnOpen = () => {
  const { state, actions } = useGroupChannel();
  const hasAutoScrolledRef = useRef(false);
  const userScrolledRef = useRef(false);

  const channelUrl = state.channelUrl;
  const scrollRef = state.scrollRef;

  useEffect(() => {
    hasAutoScrolledRef.current = false;
    userScrolledRef.current = false;
  }, [channelUrl]);

  useEffect(() => {
    const scrollEl = scrollRef?.current;
    if (!scrollEl) return;

    const markUserScroll = () => {
      if (!hasAutoScrolledRef.current) {
        userScrolledRef.current = true;
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.key === 'PageUp' ||
        event.key === 'PageDown' ||
        event.key === 'Home' ||
        event.key === 'End' ||
        event.key === ' '
      ) {
        markUserScroll();
      }
    };

    scrollEl.addEventListener('wheel', markUserScroll, { passive: true });
    scrollEl.addEventListener('touchmove', markUserScroll, { passive: true });
    scrollEl.addEventListener('keydown', onKeyDown);
    return () => {
      scrollEl.removeEventListener('wheel', markUserScroll);
      scrollEl.removeEventListener('touchmove', markUserScroll);
      scrollEl.removeEventListener('keydown', onKeyDown);
    };
  }, [scrollRef, channelUrl]);

  useEffect(() => {
    if (hasAutoScrolledRef.current || userScrolledRef.current) return;
    if (!state.initialized || state.loading) return;

    const channel = state.currentChannel;
    const willFindCreatedAt = getFirstUnreadStartingPoint(state.currentChannel ?? undefined);
    if (willFindCreatedAt == null) return;

    const firstUnread =
      state.messages.find((message) => message.createdAt === willFindCreatedAt) ??
      state.messages.find((message) => message.createdAt > willFindCreatedAt);

    const timer = window.setTimeout(() => {
      if (hasAutoScrolledRef.current || userScrolledRef.current) return;

      hasAutoScrolledRef.current = true;

      if (firstUnread) {
        void actions.scrollToMessage(firstUnread.createdAt, firstUnread.messageId, false, false);
        return;
      }

      void actions.scrollToMessage(willFindCreatedAt, 0, false, false);
    }, 150);

    return () => window.clearTimeout(timer);
  }, [
    actions,
    channelUrl,
    state.currentChannel,
    state.initialized,
    state.loading,
    state.messages,
  ]);

  return null;
};

export default SendbirdScrollToFirstUnreadOnOpen;
